import React, { useState, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import Textfield from '@atlaskit/textfield';
import AddIcon from '@atlaskit/icon/core/add';
import Form, { Field, ErrorMessage } from '@atlaskit/form';
import type { FormApi } from '@atlaskit/form';

import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider';
import { getSmartCardRenderers } from '@confluence/fabric-extension-lib';

import { useAudioBriefing } from '../../../../hooks/useAudioBriefing';

import { AdditionalResourceSelection } from './sub-components/AdditionalResourceSelection';

const i18n = defineMessages({
	focusedPlaceholder: {
		id: 'audio.audio-briefing-popup.sources-tab.focused-placeholder',
		defaultMessage: 'Paste a Confluence or Loom link...',
		description: 'Placholder in a textbox when the user has the text box focused',
	},
	unfocusedPlaceholder: {
		id: 'audio.audio-briefing-popup.sources-tab.unfocused-placeholder',
		defaultMessage: 'Add a link...',
		description: 'Placholder in a textbox when the textbox is not focused',
	},
	validationError: {
		id: 'audio.audio-briefing-popup.sources-tab.validation-error',
		defaultMessage: 'Only Confluence and Loom links are supported. More coming soon.',
		description:
			'Error message shown to the user when an invalid string or URL is pasted in to the textbox',
	},
});

const styles = cssMap({
	container: {
		flex: 1,
	},
	inputContainer: {
		paddingBlock: token('space.025'),
	},
	addIcon: {
		height: '18px',
		marginInlineStart: token('space.025'),
		borderWidth: token('border.width'),
		borderRadius: '3px',
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	textFieldContainer: {
		flexGrow: 1,
		paddingBlock: token('space.negative.400'),
	},
});

type FormValues = {
	resourceUrl: string;
};
type FormError = {
	resourceUrl?: string;
};

const InputAddIcon = (
	<Box xcss={styles.addIcon}>
		<AddIcon label="" size="small" spacing="compact" />
	</Box>
);

export const AudioBriefingPopupSourcesPanel = () => {
	const [{ additionalResources }, { addAdditionalResource, removeAdditionalResource }] =
		useAudioBriefing();
	const { formatMessage } = useIntl();

	const inputRef = useRef<HTMLInputElement>(null);

	const isMaxReached = additionalResources.length === 4;

	const [placeholder, setPlaceholder] = useState<string>(formatMessage(i18n.unfocusedPlaceholder));

	const handleNewResourceSubmit = (values: FormValues, form: FormApi<FormValues>) => {
		const { resourceUrl } = values;
		const errors = validateInput(resourceUrl);
		if (errors) {
			return errors;
		}
		form.reset();
		// Manually blur the input field
		if (inputRef.current) {
			inputRef.current.blur();
		}
		addAdditionalResource({
			url: resourceUrl,
		});
	};

	// Only submit validation. Not per input
	const validateInput = (value?: string): FormError | void => {
		const errors: FormError = {
			resourceUrl: formatMessage(i18n.validationError),
		};
		if (!value) {
			return errors;
		}
		try {
			new URL(value);
		} catch (e) {
			return errors;
		}
	};

	const smartCardProviderProps = getSmartCardRenderers();

	return (
		<Box xcss={styles.container}>
			<ConfluenceSmartCardProvider {...smartCardProviderProps}>
				<Stack space="space.075">
					{additionalResources.map((resource) => (
						<AdditionalResourceSelection
							key={resource.url}
							resource={resource}
							removeResource={removeAdditionalResource}
						/>
					))}
				</Stack>
			</ConfluenceSmartCardProvider>
			<Box xcss={styles.textFieldContainer}>
				<Form onSubmit={handleNewResourceSubmit}>
					{({ formProps }) => (
						<form {...formProps}>
							<Field
								name="resourceUrl"
								defaultValue=""
								isDisabled={isMaxReached}
								validate={() => {}}
							>
								{({ fieldProps, error }) => (
									<>
										<Textfield
											{...fieldProps}
											testId="briefing-sources-textfield"
											ref={inputRef}
											appearance="subtle"
											elemBeforeInput={InputAddIcon}
											label="Subtle"
											isInvalid={!!error}
											placeholder={placeholder}
											isCompact
											onFocus={() => setPlaceholder(formatMessage(i18n.focusedPlaceholder))}
											onBlur={() => setPlaceholder(formatMessage(i18n.unfocusedPlaceholder))}
										/>
										{error && <ErrorMessage>{error}</ErrorMessage>}
									</>
								)}
							</Field>
						</form>
					)}
				</Form>
			</Box>
		</Box>
	);
};
